import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Icon, Image, Label, Segment, Table } from 'semantic-ui-react'
import on_track from '../../../../../assets/svg//on_track_label.svg'
import at_risk from '../../../../../assets/svg/at_risk_label.svg'
import completed from '../../../../../assets/svg/completed_label.svg'
import progress from '../../../../../assets/svg/in_progress_label.svg'
import off_track from '../../../../../assets/svg/off_track_label.svg'
import upcoming from '../../../../../assets/svg/upcoming_label.svg'
import { useWindowSize } from '../../../../../hooks/useWindowSize'
import { RootState } from '../../../../../store/rootState'
import { IUserDetail } from '../../../../../types/user'
import { checkAdminStatus, checkRole, checkTeamMemberOrAdminStatus } from '../../../../../utils/checkRole'
import { currencyFormat } from '../../../../../utils/currencyFormat'
import { sortTasksByKey } from "../../../../../utils/sort"
import * as BudgetsActions from '../../../actions/budgetCostsActions'
import * as CostsActions from '../../../actions/directAndPassTroughCosts'
import ServiceProviderBudgetTable from '../../../components/budget/service-provider/ServiceProviderBudgetCostsTableComponent'
import { _initialDirectCost } from '../../../reducers/directAndPassTroughCostsReducer'
import { getClusteredDirectCostsSelector, getDirectCostsSelector } from '../../../selectors/directAndPassTroughCostsSelector'
import { ICostSearch, ICostSorting, ICostsClustering, ICostsSortColumn, IDirectCost } from '../../../types/directAndPassTroughCosts'
import { IProject, projectStatus } from '../../../types/project'
import DirectAndPassTroughCostDetailsContainer from '../../activities/DirectAndPassTroughCostDetailsContainer'

interface StateProps {
  project: IProject
  clusteredCosts: ICostsClustering[] | undefined
  costList: IDirectCost[]
  sortCosts: ICostSorting
  searchCostsValue: ICostSearch
  user: IUserDetail | null
  selectedCost: IDirectCost
}

interface DispatchProps {
  setSortCosts: (sorting: ICostSorting) => void
  setOpenDetailsModal: (open: boolean) => void
  setSelectedCost: (cost: IDirectCost) => void
  getDirectCostHistory: (projectId: number | string, cost: IDirectCost, searchQuery?: string) => void
  resetDirectCostHistory: () => void
}

type Props = StateProps & DispatchProps

const _directCostHeader = [
  { text: 'Task', textAlign: 'left' as CanvasTextAlign },
  { text: 'Phase', textAlign: 'left' as CanvasTextAlign },
  { text: 'Department', textAlign: 'left' as CanvasTextAlign },
  { text: 'Budget', textAlign: 'right' as CanvasTextAlign },
  { text: 'Expenditures', textAlign: 'right' as CanvasTextAlign },
  { text: 'Forecast', textAlign: 'right' as CanvasTextAlign },
  { text: 'Projected Profit', textAlign: 'center' as CanvasTextAlign },
  { text: 'Health', textAlign: 'left' as CanvasTextAlign },
  { text: 'Status', textAlign: 'left' as CanvasTextAlign }
]

const ServiceProviderDirectCostsContainer = (props: Props): JSX.Element => {
  const {
    clusteredCosts,
    costList,
    project,
    sortCosts,
    user,
    searchCostsValue,
    selectedCost,
    setSortCosts,
    setOpenDetailsModal,
    setSelectedCost,
    getDirectCostHistory,
    resetDirectCostHistory
  } = props

  const [sort, setSort] = useState(sortCosts.column)
  const [costListState, setCostListState] = useState(costList)
  const [editRight, setEditRight] = useState(false)
  const _size = useWindowSize()

  useEffect(() => {
    if (user) {
      setEditRight(checkRole(user, [15]) && checkTeamMemberOrAdminStatus(user, project))
    }
  }, [project, user])

  useEffect(() => {
    if (project.id) {
      if (selectedCost.id !== '') {
        setSelectedCost(selectedCost)
        getDirectCostHistory(project.id, selectedCost)
      } else {
        resetDirectCostHistory()
        setSelectedCost(_initialDirectCost)
      }
    }
  }, [selectedCost, project.id, setOpenDetailsModal, setSelectedCost, getDirectCostHistory, resetDirectCostHistory])

  useEffect(() => {
    let _costs = costList
    if (searchCostsValue.search !== '') {
      _costs = costList.filter(
        dc => dc.offer_position.toLowerCase().includes(searchCostsValue.search.toLowerCase())
      )
    }
    setCostListState(_costs)
  }, [searchCostsValue, costList])

  const calcTotal = (costs: IDirectCost[], key: 'budget' | 'expenditures' | 'forecast' | 'profit' | 'external_expenditures') => {
    return costs.map(a => a[key] ?? 0).reduce((a, b) => a + b, 0)
  }

  const calcTotalBillableAmount = (costs: IDirectCost[]): number => {
    return costs.map(dc => dc.billable_amount ? +dc.billable_amount : 0).reduce((a, b) => a + b, 0)
  }

  const calcProfit = (costs: IDirectCost[]): number => {
    const total_forecast = costs.map(a => a.forecast ?? 0).reduce((a, b) => a + b, 0)
    const total_budget = costs.map(a => a.budget ?? 0).reduce((a, b) => a + b, 0)
    return total_budget !== 0 ? ((total_budget - total_forecast) / total_budget) * 100 : 0
  }

  const calcPhaseStatus = (costs: IDirectCost[]) => {
    if (!costs.some(c => c.status !== 'upcoming')) {
      return 'upcoming'
    } else if (!costs.some(c => c.status !== 'completed')) {
      return 'completed'
    } else if (costs.some(c => c.status === 'in_progress')) {
      return 'in_progress'
    }
    return 'in_progress'
  }

  const calcStatus = (costs: IDirectCost[]) => {
    const completed: IDirectCost[] = []
    const upcoming: IDirectCost[] = []
    const in_progress: IDirectCost[] = []
    costs.map(c => {
      if (c.status === 'completed') {
        completed.push(c)
      } else if (c.status === 'upcoming') {
        upcoming.push(c)
      } else if (c.status === 'in_progress') {
        in_progress.push(c)
      }
    })
    const counter: IDirectCost = completed.length > upcoming.length ? completed[0] : upcoming.length > in_progress.length ? upcoming[0] : in_progress[0]
    return counter
  }

  const calcHealth = (costs: IDirectCost[]) => {
    const costsStatus = calcStatus(costs)
    const budget = calcTotal(costs, 'budget')
    const forecast = calcTotal(costs, 'forecast')
    const expenditures = calcTotal(costs, 'expenditures')
    let health = ''
    if (costsStatus.status !== 'completed') {
      if (forecast <= budget) {
        health = 'on_track'
      }
      if (forecast > budget) {
        health = 'at_risk'
      }
      if (expenditures > budget) {
        health = 'off_track'
      }
    } else {
      if (expenditures <= budget) {
        health = 'on_track'
      }
      if (expenditures > budget) {
        health = 'off_track'
      }
    }
    return health
  }

  const calcSinglHealth = (dc: IDirectCost) => {
    const budget = dc.budget ?? 0
    const forecast = dc.forecast ?? 0
    let health = ''
    if (dc.status !== 'completed') {
      if (forecast <= budget) {
        health = 'on_track'
      }
      if (forecast > budget) {
        health = 'at_risk'
      }
      if (dc.expenditures > budget) {
        health = 'off_track'
      }
    } else {
      if (dc.expenditures <= budget) {
        health = 'on_track'
      }
      if (dc.expenditures > budget) {
        health = 'off_track'
      }
    }
    return health
  }

  const renderStatusOrHealthLabel = (costs: IDirectCost[], key: 'status' | 'health' | 'phase_status') => {
    const costsStatus = calcStatus(costs)
    const phaseStatus = calcPhaseStatus(costs)
    if (costsStatus) {
      const _status = key === 'status' ? costsStatus.status : key === 'phase_status' ? phaseStatus : ''
      return (
        <Label
          image
          style={{
            backgroundColor: costsStatus.statusColor(key === 'health' ? calcHealth(costs) : _status).background,
            color: costsStatus.statusColor(key === 'health' ? calcHealth(costs) : _status).color,
            borderRadius: '8px',
            height: '26px',
          }}>
          <Image
            src={
              key === 'health' ? calcHealth(costs) === 'on_track' ? on_track : calcHealth(costs) === 'at_risk' ? at_risk : calcHealth(costs) === 'off_track' ? off_track : undefined
                : _status === 'completed' ? completed : _status === 'in_progress' ? progress : _status === 'upcoming' ? upcoming : undefined
            }
            style={{ maxWidth: '12px', marginLeft: '2px' }}
          />
          {key === 'health' ? projectStatus.find(s => s.key === calcHealth(costs))?.name : projectStatus.find(s => s.key === _status)?.name}
        </Label>
      )
    }
    return null
  }

  const onClick = (sortBy: ICostsSortColumn['name']) => {
    const sorting = sortCosts
    sorting.column.direction = sortBy !== sortCosts.column.name ? 'asc' : sortCosts.column.direction === 'asc' ? 'desc' : 'asc'
    sorting.column.name = sortBy
    setSortCosts(sorting)
    setSort({ direction: sorting.column.direction, name: sorting.column.name })
  }

  const renderTableHeader = () => {
    let _header = _directCostHeader.filter(d => d.text !== 'Status')
    if (project.billing_procedure === 'billing_on_actual' && _directCostHeader.find(dch => dch.text === 'Billable Amount') === undefined) {
      _header.splice(4, 0, { text: 'Billable Amount', textAlign: 'right' as CanvasTextAlign })
    } else if (project.billing_procedure !== 'billing_on_actual' && _directCostHeader.find(dch => dch.text === 'Billable Amount')) {
      _header = _directCostHeader.filter(d => d.text !== 'Billable Amount')
    }
    return _header.map((h, idx) => {
      const click = h.text === 'Task' ? 'offer_position' : h.text.toLowerCase()
      return (
        <Table.Cell
          key={idx}
          colSpan={h.text === 'Task' ? 3 : 1}>
          <div
            className={h.text === 'Health' ? 'last-visable-table-cell' : ''}
            style={{
              display: h.text === 'Health' && _size.width <= 1455 && project.billing_procedure === 'billing_on_actual' ? 'none' : 'flex',
              alignItems: 'baseline',
              justifyContent: h.textAlign === 'center' ? 'center' : h.textAlign === 'left' ? 'flex-start' : 'flex-end',
              paddingLeft: h.text === 'Task' ? '40px' : 0,
              paddingRight: h.text === 'Forecast' ? '4px' : 0,
              whiteSpace: 'nowrap'
            }}
          >
            <div style={{ fontSize: '14px', color: '#747678' }}>{h.text}</div>
            <div
              style={{ display: 'flex', flexDirection: 'column' }}
              onClick={() => onClick(click as ICostsSortColumn['name'])}
            >
              <Icon
                name='sort up'
                style={{
                  color: '#747678',
                  opacity: sort.name === click && sort.direction === 'asc' ? 0.4 : 1,
                  fontSize: '10px'
                }}
              />
              <Icon
                name='sort down'
                style={{
                  marginTop: '-10px',
                  color: '#747678',
                  opacity: sort.name === click && sort.direction === 'desc' ? 0.4 : 1,
                  fontSize: '10px'
                }}
              />
            </div>
          </div>
        </Table.Cell>
      )
    })
  }

  const renderTableBody = () => {
    if (clusteredCosts) {
      return clusteredCosts.map((cluster, idx) => {
        if (cluster.costs.length !== 0) {
          const _costs = sortedCosts(cluster.costs as IDirectCost[])
          if (_costs.length !== 0) {
            return (
              <ServiceProviderBudgetTable
                idx={idx}
                key={cluster.name ? cluster.name : ''}
                billingProcedure={project.billing_procedure}
                costs={_costs}
                name={cluster.name ? cluster.name : ''}
                color={cluster.phase?.color}
                renderHealthLabel={renderStatusOrHealthLabel(_costs, 'health')}
                calcTotalBillableAmount={calcTotalBillableAmount(_costs)}
                calcExpenditures={calcTotal(_costs, 'expenditures') + calcTotal(_costs, 'external_expenditures')}
                calcForecast={calcTotal(_costs, 'forecast')}
                calcProfit={calcProfit(_costs)}
                calcBudget={calcTotal(_costs, 'budget')}
                statusCompleted={calcStatus(_costs) && calcStatus(_costs).status === 'completed' ? true : false}
                setOpenDetailsModal={setOpenDetailsModal}
                setSelectedCost={setSelectedCost}
                selectedCost={selectedCost}
                editRight={editRight}
              />
            )
          }
          return null
        }
        return null
      })
    }
    return null
  }

  const renderTableFooter = () => {
    return (
      <Table.Row>
        <Table.Cell colSpan={5}><p style={{ color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold', paddingLeft: '40px' }}>Total</p></Table.Cell>
        <Table.Cell textAlign='right'>
          {calcTotal(costListState, 'budget') !== 0 ?
            <div style={{ display: 'flex', float: 'right', color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>
              <p style={{ margin: 0 }}>{currencyFormat(calcTotal(costListState, 'budget'))[0]} {currencyFormat(calcTotal(costListState, 'budget')).split(',')[0].replace(currencyFormat(calcTotal(costListState, 'budget'))[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotal(costListState, 'budget')).split(',')[1]}</p>
            </div>
            : <p style={{ color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>- €</p>}
        </Table.Cell>
        {project.billing_procedure === 'billing_on_actual' && <Table.Cell textAlign='right'>
          <div style={{ display: 'flex', float: 'right', color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcTotalBillableAmount(costList))[0]} {currencyFormat(calcTotalBillableAmount(costList)).split(',')[0].replace(currencyFormat(calcTotalBillableAmount(costList))[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotalBillableAmount(costList)).split(',')[1]}</p>
          </div>
        </Table.Cell>}
        <Table.Cell textAlign='right'>
          <div style={{ display: 'flex', float: 'right', color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcTotal(costList, 'expenditures') + calcTotal(costList, 'external_expenditures'))[0]} {currencyFormat(calcTotal(costList, 'expenditures') + calcTotal(costList, 'external_expenditures')).split(',')[0].replace(currencyFormat(calcTotal(costList, 'expenditures') + calcTotal(costList, 'external_expenditures'))[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotal(costList, 'expenditures') + calcTotal(costList, 'external_expenditures')).split(',')[1]}</p>
          </div>
        </Table.Cell>
        <Table.Cell textAlign='right' className={_size.width <= 1400 ? 'last-visable-table-cell' : ''}>
          <div style={{ paddingRight: '4px', display: 'flex', float: 'right', color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>
            <p style={{ margin: 0 }}>{currencyFormat(calcTotal(costList, 'forecast'))[0]} {currencyFormat(calcTotal(costList, 'forecast')).split(',')[0].replace(currencyFormat(calcTotal(costList, 'forecast'))[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotal(costList, 'forecast')).split(',')[1]}</p>
          </div>
        </Table.Cell>
        {_size.width > 1560 && <Table.Cell textAlign='center'>
          {calcTotal(costListState, 'profit') !== 0 ?
            <p style={{ color: calcStatus(costListState).status === 'completed' ? '#172533' : '#747678', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>{calcProfit(costListState).toFixed(2)} %</p>
            : <p style={{ color: '#172533', letterSpacing: '.25px', fontSize: '15px', fontWeight: 'bold' }}>- %</p>}
        </Table.Cell>}
        {_size.width > 1700 && <Table.Cell textAlign='left'>{renderStatusOrHealthLabel(costListState, 'health')}</Table.Cell>}
      </Table.Row>
    )
  }

  const renderTable = () => {
    return (
      <Segment style={{ padding: '0', margin: '0 0 1rem 0', border: 'none', borderRadius: '.57rem', boxShadow: '0 0 25px -5px #15151511' }}>
        <Table unstackable className='borderless-table-header custom-padding' padded style={{ border: 'none' }}>
          <Table.Header>
            <Table.Row>
              {renderTableHeader()}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {renderTableBody()}
          </Table.Body>
          <Table.Footer>
            {renderTableFooter()}
          </Table.Footer>
        </Table>
      </Segment>
    )
  }

  const sortedCosts = (dcs: IDirectCost[]) => {
    // asc
    let _dcs = [...dcs]
    if (sort.name === 'department') {
      _dcs.sort((a, b) => (a.department?.name ?? '').localeCompare(b.department?.name ?? ''))
    } else if (sort.name === 'offer_position') {
      _dcs = sortTasksByKey(_dcs, 'key', 'offer_position')
    } else if (sort.name === 'phase') {
      _dcs.sort((a, b) => a.project_phase.name.localeCompare(b.project_phase.name))
    } else if (sort.name === 'status') {
      _dcs.sort((a, b) => a.status.localeCompare(b.status))
    } else if (sort.name === 'expenditures') {
      _dcs.sort((a, b) => {
        let _aValue = a.expenditures ? +a.expenditures : 0
        let _bValue = b.expenditures ? +b.expenditures : 0
        if (a.external_expenditures) {
          _aValue += +a.external_expenditures
        }
        if (b.external_expenditures) {
          _bValue += +b.external_expenditures
        }
        return _aValue - _bValue
      })
    } else if (sort.name === 'forecast') {
      _dcs.sort((a, b) => (a.forecast ?? 0) - (b.forecast ?? 0))
    } else if (sort.name === 'budget') {
      _dcs.sort((a, b) => (a.budget ?? 0) - (b.budget ?? 0))
    } else if (sort.name === 'projected profit') {
      _dcs.sort((a, b) => (a.profit ?? 0) - (b.profit ?? 0))
    } else if (sort.name === 'billable amount') {
      _dcs.sort((a, b) => (a.billable_amount ?? 0) - (b.billable_amount ?? 0))
    } else if (sort.name === 'health') {
      _dcs.sort((a, b) => calcSinglHealth(a).localeCompare(calcSinglHealth(b)))
    }
    // desc
    if (sort.direction === 'desc') {
      _dcs.reverse()
    }
    return _dcs
  }

  return (
    <React.Fragment>
      {user && (checkRole(user, [16]) || checkAdminStatus(user, project)) ? renderTable() : null}
      {props.selectedCost.id !== '' && props.selectedCost.project_phase ? <DirectAndPassTroughCostDetailsContainer /> : null}
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    project: state.projectsState.project,
    clusteredCosts: getClusteredDirectCostsSelector(state),
    costList: getDirectCostsSelector(state),
    sortCosts: state.projectsState.sortCosts,
    user: state.authState.user,
    searchCostsValue: state.projectsState.searchCosts,
    selectedCost: state.projectsState.selectedCost as IDirectCost,
  }
}

const mapDispatchToProps = (dispatch: CostsActions.DirectAndPassTroughCostThunkDispatchType & BudgetsActions.BudgetCostsThunkDispatchType): DispatchProps => {
  return {
    setSortCosts: sorting => dispatch(CostsActions.sortCosts(sorting)),
    setOpenDetailsModal: (open) => dispatch(CostsActions.setOpenCostDetailsModal(open)),
    setSelectedCost: cost => dispatch(CostsActions.setSelectedCost(cost)),
    getDirectCostHistory: (projectId, cost, search) => dispatch(CostsActions.getDirectCostHistory(projectId, cost, search)),
    resetDirectCostHistory: () => dispatch(CostsActions.resetDirectCostHistory()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ServiceProviderDirectCostsContainer)
